/* Demo-only helpers. The MockProvider exposes device simulators
 * (simulateDeviceHandshake / simulateDevicePush / simulateWebhook) and its
 * injected clock; the HttpProvider has neither. Pages never import the
 * MockProvider — they detect these helpers with a type guard.
 * Pages read the business clock with useClock()/useToday() from data/hooks. */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useData } from "../../../data";
import { PUNCH_KEYS, qk } from "../../../data/hooks";
import type { DataProvider } from "../../../data/provider";
import type { Device, IngestResult } from "../../../data/types";

export interface MockSim {
  readonly mode: "mock";
  simulateDeviceHandshake(serial: string): Promise<string>;
  simulateDevicePush(serial: string, attlogBody: string, stamp?: string): Promise<{ reply: string; result: IngestResult }>;
  simulateWebhook(deviceId: string, token: string, payload: unknown): Promise<IngestResult>;
}

/** Returns the mock simulators, or null in http mode (never show demo UI then). */
export function mockSim(p: DataProvider): MockSim | null {
  if (p.mode !== "mock") return null;
  const x = p as unknown as Partial<MockSim>;
  if (typeof x.simulateDevicePush !== "function" || typeof x.simulateWebhook !== "function" || typeof x.simulateDeviceHandshake !== "function") return null;
  return x as MockSim;
}

/** ISO timestamp shifted by `sec` seconds (wall clock, no zone). */
export function shiftIso(iso: string, sec: number): string {
  const t = Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10), +iso.slice(11, 13), +iso.slice(14, 16), +(iso.slice(17, 19) || 0)) + sec * 1000;
  return new Date(t).toISOString().slice(0, 19);
}

let seq = 0;

export interface TestPunchResult { result: IngestResult; employeeId: number | null; employee: { nameEn: string; nameAr: string } | null; deviceUserId: string; time: string; direction: "in" | "out" }

/** Can this device receive a demo punch? (push always; webhook/agent need the unmasked token). */
export function canDemo(d: Pick<Device, "protocol" | "status">, token?: string | null): boolean {
  if (d.status === "pending") return false;
  if (d.protocol === "push") return true;
  return (d.protocol === "webhook" || d.protocol === "agent") && !!token && !token.startsWith("••••");
}

/** Demo hook: sends a realistic test punch from the device (mock only) and refreshes attendance. */
export function useDemoPunch() {
  const p = useData();
  const qc = useQueryClient();
  const sim = mockSim(p);
  const send = useCallback(async (d: Device, token?: string | null): Promise<TestPunchResult> => {
    if (!sim) throw new Error("demo only");
    // Pick someone whose day a punch visibly changes: a missing check-out first, then an absentee.
    const clk = await p.clock();
    const day = await p.attendanceDay(clk.today);
    const row = day.rows.find((r) => r.status === "Missing punch") ?? day.rows.find((r) => r.status === "Absent" || r.status === "Not in yet") ?? day.rows[0];
    const direction: "in" | "out" = row && row.firstIn ? "out" : "in";
    const maps = await p.listMappings();
    const m = row ? maps.find((x) => x.employeeId === row.employeeId && (x.deviceId === d.id || x.deviceId === "*")) : undefined;
    const user = m ? m.deviceUserId : row ? String(row.employeeId) : "1";
    const time = shiftIso(clk.now, -seq++);
    let result: IngestResult;
    if (d.protocol === "push") {
      await sim.simulateDeviceHandshake(d.serial);
      result = (await sim.simulateDevicePush(d.serial, `${user}\t${time.replace("T", " ")}\t${direction === "in" ? 0 : 1}\t15\t0`)).result;
    } else {
      result = await sim.simulateWebhook(d.id, token || "", { deviceUserId: user, time, direction, verify: "face" });
    }
    await Promise.all([...PUNCH_KEYS, qk.commands].map((queryKey) => qc.invalidateQueries({ queryKey })));
    let employee: TestPunchResult["employee"] = null;
    if (m) { try { const e = await p.getEmployee(m.employeeId); employee = { nameEn: e.nameEn, nameAr: e.nameAr }; } catch { employee = null; } }
    return { result, employeeId: m ? m.employeeId : null, employee, deviceUserId: user, time, direction };
  }, [p, qc, sim]);
  /** A brand-new push device knocks on the door (shows up under Pending devices). */
  const handshake = useCallback(async (serial: string) => {
    if (!sim) throw new Error("demo only");
    await sim.simulateDeviceHandshake(serial);
    await Promise.all([qk.devices, qk.bioOverview].map((queryKey) => qc.invalidateQueries({ queryKey })));
  }, [qc, sim]);
  return { enabled: !!sim, send, handshake };
}
